import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, ShieldAlert, Check, X, Link2, MessageCircle, CalendarPlus, Compass, Ship, Clock, AlertTriangle, History, Plus, Trash2, Ban, UserX } from 'lucide-react';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KIND_META = {
  chat: { label: 'Chat di gruppo', icon: MessageCircle, color: 'bg-cyan-100 text-cyan-700' },
  dm: { label: 'Messaggio privato', icon: MessageCircle, color: 'bg-violet-100 text-violet-700' },
  event: { label: 'Evento', icon: CalendarPlus, color: 'bg-orange-100 text-orange-700' },
  excursion: { label: 'Escursione', icon: Compass, color: 'bg-emerald-100 text-emerald-700' },
};

const REASON_META = {
  link: { label: 'Link', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  profanity: { label: 'Linguaggio offensivo', color: 'bg-rose-100 text-rose-700 border-rose-200' },
};

const ModerationCommunity = () => {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [words, setWords] = useState([]);
  const [newWord, setNewWord] = useState('');
  const [offenders, setOffenders] = useState([]);
  const [ships, setShips] = useState([]);
  const [shipSug, setShipSug] = useState([]);
  const [shipForm, setShipForm] = useState({ ship_name: '', image_url: '' });

  const load = async (which = tab) => {
    setLoading(true);
    try {
      if (which === 'blocklist') {
        const { data } = await axios.get(`${API}/admin/community-blocklist`);
        setWords(data.words);
      } else if (which === 'offenders') {
        const { data } = await axios.get(`${API}/admin/community-offenders?threshold=3`);
        setOffenders(data.offenders);
      } else if (which === 'ships') {
        const { data } = await axios.get(`${API}/admin/community-ships`);
        setShips(data.items);
        setShipSug(data.suggestions);
      } else {
        const status = which === 'history' ? 'history' : 'pending';
        const { data } = await axios.get(`${API}/admin/community-moderation?status=${status}`);
        setItems(data.items);
      }
    } catch (e) {
      toast.error('Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  const saveShip = async () => {
    if (!shipForm.ship_name.trim() || !shipForm.image_url.trim()) return toast.error('Compila nome e URL');
    try {
      await axios.post(`${API}/admin/community-ships`, shipForm);
      setShipForm({ ship_name: '', image_url: '' });
      toast.success('Foto nave salvata');
      load('ships');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const removeShip = async (key) => {
    try {
      await axios.delete(`${API}/admin/community-ships/${key}`);
      load('ships');
    } catch (e) {
      toast.error('Errore');
    }
  };

  useEffect(() => {
    load(tab);
  }, [tab]);

  const addWord = async () => {
    const w = newWord.trim().toLowerCase();
    if (!w) return;
    try {
      const { data } = await axios.post(`${API}/admin/community-blocklist/add`, { word: w });
      setWords(data.words);
      setNewWord('');
      toast.success('Parola aggiunta');
    } catch (e) {
      toast.error('Errore');
    }
  };

  const removeWord = async (w) => {
    try {
      const { data } = await axios.post(`${API}/admin/community-blocklist/remove`, { word: w });
      setWords(data.words);
    } catch (e) {
      toast.error('Errore');
    }
  };

  const toggleSuspend = async (o) => {
    const action = o.suspended ? 'unsuspend' : 'suspend';
    try {
      const { data } = await axios.post(`${API}/admin/community-members/${o.member_id}/${action}`);
      setOffenders((prev) => prev.map((x) => (x.member_id === o.member_id ? { ...x, suspended: data.suspended } : x)));
      toast.success(data.message);
    } catch (e) {
      toast.error('Operazione non riuscita');
    }
  };

  const act = async (id, action) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/community-moderation/${id}/${action}`);
      toast.success(action === 'approve' ? 'Contenuto approvato e pubblicato' : 'Contenuto rifiutato ed eliminato');
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      toast.error('Operazione non riuscita');
    } finally {
      setBusy(null);
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <Link to="/dashboard" className="text-cyan-600 hover:text-cyan-800 text-sm flex items-center gap-1 mb-2" data-testid="mod-back-link">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-rose-500" /> Moderazione Community
          </h1>
          <p className="text-slate-500 text-sm mt-1">Approva o rifiuta i contenuti segnalati (link e linguaggio offensivo).</p>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setTab('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'pending' ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid="mod-tab-pending"
            >
              In attesa
            </button>
            <button
              onClick={() => setTab('history')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${tab === 'history' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid="mod-tab-history"
            >
              <History className="w-4 h-4" /> Storico
            </button>
            <button
              onClick={() => setTab('blocklist')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${tab === 'blocklist' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid="mod-tab-blocklist"
            >
              <Ban className="w-4 h-4" /> Parole bloccate
            </button>
            <button
              onClick={() => setTab('offenders')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${tab === 'offenders' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid="mod-tab-offenders"
            >
              <UserX className="w-4 h-4" /> Recidivi
            </button>
            <button
              onClick={() => setTab('ships')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${tab === 'ships' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid="mod-tab-ships"
            >
              <Ship className="w-4 h-4" /> Foto Navi
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <p className="text-center text-slate-400 py-10">Caricamento...</p>
        ) : tab === 'blocklist' ? (
          <Card>
            <CardContent className="p-5">
              <p className="text-sm text-slate-500 mb-3">Aggiungi o rimuovi le parole che attivano una segnalazione all'admin.</p>
              <div className="flex gap-2 mb-4">
                <Input
                  data-testid="blocklist-input"
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addWord()}
                  placeholder="Nuova parola da bloccare..."
                />
                <Button className="bg-slate-700 hover:bg-slate-800" onClick={addWord} data-testid="blocklist-add-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Aggiungi
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {words.map((w) => (
                  <span key={w} className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full text-sm" data-testid={`blocklist-word-${w}`}>
                    {w}
                    <button onClick={() => removeWord(w)} className="text-slate-400 hover:text-rose-500" data-testid={`blocklist-remove-${w}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
                {words.length === 0 && <p className="text-slate-400 text-sm">Nessuna parola in lista.</p>}
              </div>
            </CardContent>
          </Card>
        ) : tab === 'offenders' ? (
          offenders.length === 0 ? (
            <div className="text-center py-16 text-slate-400 bg-white rounded-xl border border-dashed" data-testid="offenders-empty">
              <Check className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
              Nessun membro recidivo (soglia: 3 contenuti rifiutati).
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Membri con almeno 3 contenuti rifiutati — valuta un intervento.</p>
              {offenders.map((o) => (
                <Card key={o.member_id} data-testid={`offender-${o.member_id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${o.suspended ? 'bg-slate-200 text-slate-500' : 'bg-rose-100 text-rose-600'}`}>
                        <UserX className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 flex items-center gap-2">
                          {o.name}
                          {o.suspended && <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Sospeso</span>}
                        </p>
                        {o.email && <p className="text-xs text-slate-500">{o.email}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="bg-rose-500 text-white text-sm font-bold px-3 py-1 rounded-full">{o.rejected_count} rifiutati</span>
                      <Button
                        size="sm"
                        variant={o.suspended ? 'outline' : 'default'}
                        className={o.suspended ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'bg-slate-700 hover:bg-slate-800'}
                        onClick={() => toggleSuspend(o)}
                        data-testid={`suspend-btn-${o.member_id}`}
                      >
                        {o.suspended ? <><Check className="w-4 h-4 mr-1.5" /> Riattiva</> : <><Ban className="w-4 h-4 mr-1.5" /> Sospendi</>}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : tab === 'ships' ? (
          <Card>
            <CardContent className="p-5">
              <p className="text-sm text-slate-500 mb-3">Aggiungi la foto di una nave tramite link: il sistema la userà come sfondo di tutte le card delle crociere con quel nome.</p>
              <div className="grid sm:grid-cols-[1fr_1.5fr_auto] gap-2 mb-4">
                <Input
                  data-testid="ship-name-input"
                  list="ship-suggestions"
                  value={shipForm.ship_name}
                  onChange={(e) => setShipForm({ ...shipForm, ship_name: e.target.value })}
                  placeholder="Nome nave (es. MSC Seaview)"
                />
                <datalist id="ship-suggestions">
                  {shipSug.map((s) => <option key={s} value={s} />)}
                </datalist>
                <Input
                  data-testid="ship-url-input"
                  value={shipForm.image_url}
                  onChange={(e) => setShipForm({ ...shipForm, image_url: e.target.value })}
                  placeholder="URL immagine (https://...)"
                />
                <Button className="bg-slate-700 hover:bg-slate-800" onClick={saveShip} data-testid="ship-save-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Salva
                </Button>
              </div>
              {ships.length === 0 ? (
                <p className="text-slate-400 text-sm">Nessuna foto nave ancora. Aggiungi la prima!</p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {ships.map((s) => (
                    <div key={s.ship_key} className="relative rounded-lg overflow-hidden border border-slate-200 group" data-testid={`ship-item-${s.ship_key}`}>
                      <img src={s.image_url} alt={s.ship_name} className="w-full h-28 object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-2 flex items-center justify-between">
                        <span className="text-white font-semibold text-sm flex items-center gap-1"><Ship className="w-3.5 h-3.5" /> {s.ship_name}</span>
                        <button onClick={() => removeShip(s.ship_key)} className="bg-white/90 hover:bg-rose-500 hover:text-white text-slate-600 rounded-full p-1" data-testid={`ship-remove-${s.ship_key}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-slate-400 bg-white rounded-xl border border-dashed" data-testid="mod-empty">
            <Check className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
            {tab === 'pending' ? 'Nessuna segnalazione in attesa. Tutto sotto controllo! 🎉' : 'Nessuna decisione nello storico.'}
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((it) => {
              const meta = KIND_META[it.kind] || KIND_META.chat;
              const Icon = meta.icon;
              const reasons = it.reasons || (it.links && it.links.length ? ['link'] : []);
              const flagged = it.flagged_words || [];
              return (
                <Card key={it.id} data-testid={`mod-item-${it.id}`} className="overflow-hidden">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>
                            <Icon className="w-3.5 h-3.5" /> {meta.label}
                          </span>
                          {reasons.map((r) => (
                            <span key={r} className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${(REASON_META[r] || {}).color || 'bg-slate-100 text-slate-600'}`}>
                              {r === 'profanity' ? <AlertTriangle className="w-3 h-3" /> : <Link2 className="w-3 h-3" />}
                              {(REASON_META[r] || {}).label || r}
                            </span>
                          ))}
                          {it.status === 'approved' && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Approvato</span>}
                          {it.status === 'rejected' && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Rifiutato</span>}
                          <span className="text-sm font-semibold text-slate-700">{it.author_name}</span>
                          {it.cruise && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Ship className="w-3.5 h-3.5" /> {it.cruise.ship_name} · {it.cruise.departure_city}
                            </span>
                          )}
                          <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(it.created_at)}</span>
                        </div>

                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{it.original_text}</p>
                        </div>

                        {it.links && it.links.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Link da verificare:</p>
                            <div className="flex flex-wrap gap-2">
                              {it.links.map((l, idx) => (
                                <a
                                  key={idx}
                                  href={l.startsWith('http') ? l : `http://${l}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs bg-rose-50 text-rose-700 border border-rose-200 px-2 py-1 rounded-md hover:underline break-all"
                                  data-testid={`mod-link-${it.id}-${idx}`}
                                >
                                  {l}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                        {flagged.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Parole segnalate:</p>
                            <div className="flex flex-wrap gap-2">
                              {flagged.map((w, idx) => (
                                <span key={idx} className="text-xs bg-rose-50 text-rose-700 border border-rose-200 px-2 py-1 rounded-md">{w}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {tab === 'pending' && (
                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => act(it.id, 'approve')}
                            disabled={busy === it.id}
                            data-testid={`mod-approve-${it.id}`}
                          >
                            <Check className="w-4 h-4 mr-1.5" /> Approva
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-rose-300 text-rose-600 hover:bg-rose-50"
                            onClick={() => act(it.id, 'reject')}
                            disabled={busy === it.id}
                            data-testid={`mod-reject-${it.id}`}
                          >
                            <X className="w-4 h-4 mr-1.5" /> Rifiuta
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModerationCommunity;
